/**
 * FeedbackDetailModal Component
 *
 * Modal for viewing detailed feedback information and updating status.
 * Shows full message context with the EXACT same rendering as the chat.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Turtle, XCircle, Lightbulb, MessageSquare, Clock,
  RefreshCw, CheckCircle, XOctagon, User, Calendar, Bot,
  Save, Loader, Table, BarChart3, Trash2, AlertTriangle
} from 'lucide-react';
import { useFeedback } from '../hooks/useFeedback';
import { useSettings } from '../hooks/useSettings';
import DataVisualization from './MessageBubble/DataVisualization';
import SQLDisplay from './MessageBubble/SQLDisplay';
import DataTable from './MessageBubble/DataTable';
import ContextualSuggestions from './MessageBubble/ContextualSuggestions';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled(motion.div)`
  background: #f0f4f8;
  border-radius: 16px;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  border-radius: 16px 16px 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Content = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  svg {
    width: 16px;
    height: 16px;
    color: #1B8C3C;
  }
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const IssueTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: ${props => {
    switch (props.$type) {
      case 'too_slow': return '#feebc8';
      case 'wrong_answer': return '#fed7d7';
      case 'could_be_better': return '#c6f6d5';
      case 'other': return '#e2e8f0';
      default: return '#e2e8f0';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'too_slow': return '#c05621';
      case 'wrong_answer': return '#c53030';
      case 'could_be_better': return '#276749';
      case 'other': return '#4a5568';
      default: return '#4a5568';
    }
  }};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: ${props => {
    switch (props.$status) {
      case 'open': return '#bee3f8';
      case 'in_progress': return '#fefcbf';
      case 'resolved': return '#c6f6d5';
      case 'wont_fix': return '#e2e8f0';
      default: return '#e2e8f0';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'open': return '#2b6cb0';
      case 'in_progress': return '#975a16';
      case 'resolved': return '#276749';
      case 'wont_fix': return '#4a5568';
      default: return '#4a5568';
    }
  }};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const InfoItem = styled.div`
  background: white;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;

  .label {
    font-size: 11px;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .value {
    font-size: 14px;
    color: #2d3748;
    font-weight: 500;
  }
`;

const FeedbackCommentBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;

  .feedback-label {
    font-size: 12px;
    font-weight: 600;
    color: #856404;
    text-transform: uppercase;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .feedback-text {
    color: #856404;
    font-size: 14px;
    line-height: 1.6;
  }
`;

/* Chat-like message display */
const ChatPreviewContainer = styled.div`
  background: #f0f4f8;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`;

const UserMessageBubble = styled.div`
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  color: white;
  padding: 16px 20px;
  border-radius: 20px 20px 4px 20px;
  max-width: 80%;
  margin-left: auto;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(27,140,60, 0.3);

  .content {
    font-weight: 500;
    line-height: 1.5;
    direction: auto;
    text-align: start;
    unicode-bidi: plaintext;
  }

  .timestamp {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    margin-top: 4px;
  }
`;

const BotMessageContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4FB8B8 0%, #1B8C3C 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;

const BotBubble = styled.div`
  background: white;
  padding: 20px;
  border-radius: 20px 20px 20px 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  flex: 1;
  min-width: 0;

  .main-content {
    color: #2d3748;
    line-height: 1.6;
    margin-bottom: 16px;
    font-weight: 500;
    direction: auto;
    text-align: start;
    unicode-bidi: plaintext;
    white-space: pre-wrap;
  }

  .timestamp {
    font-size: 12px;
    color: #a0aec0;
    margin-top: 8px;
  }
`;

const DataSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;

  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 12px;

    svg {
      width: 16px;
      height: 16px;
      color: #1B8C3C;
    }
  }
`;

const VisualizationSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;

  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 12px;

    svg {
      width: 16px;
      height: 16px;
      color: #1B8C3C;
    }
  }
`;

const StatusUpdateSection = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 16px;
  margin-top: 24px;
`;

const StatusSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 12px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #1B8C3C;
  }
`;

const ResolutionTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 12px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:focus {
    outline: none;
    border-color: #1B8C3C;
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

const UpdateButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #1B8C3C;

  svg {
    width: 32px;
    height: 32px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #e53e3e;
`;

const DeleteButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 12px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ConfirmDialog = styled(motion.div)`
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
`;

const ConfirmMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;

  svg {
    width: 24px;
    height: 24px;
    color: #e53e3e;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .text {
    color: #c53030;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ConfirmButton = styled(motion.button)`
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &.cancel {
    background: white;
    border: 1px solid #e2e8f0;
    color: #4a5568;

    &:hover {
      background: #f7fafc;
    }
  }

  &.confirm {
    background: #e53e3e;
    border: none;
    color: white;

    &:hover {
      background: #c53030;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ResolutionInfo = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  margin-top: 16px;
`;

const ISSUE_TYPE_ICONS = {
  too_slow: Turtle,
  wrong_answer: XCircle,
  could_be_better: Lightbulb,
  other: MessageSquare
};

const STATUS_ICONS = {
  open: Clock,
  in_progress: RefreshCw,
  resolved: CheckCircle,
  wont_fix: XOctagon
};

function FeedbackDetailModal({ isOpen, onClose, feedbackId, onUpdate }) {
  const [newStatus, setNewStatus] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const {
    selectedFeedback,
    loading,
    error,
    getFeedbackDetail,
    updateFeedback,
    deleteFeedback
  } = useFeedback();

  const { language, isRTL } = useSettings();
  const isAdmin = localStorage.getItem('user_role') === 'admin';

  useEffect(() => {
    if (isOpen && feedbackId) {
      getFeedbackDetail(feedbackId);
    }
  }, [isOpen, feedbackId, getFeedbackDetail]);

  useEffect(() => {
    if (selectedFeedback) {
      setNewStatus(selectedFeedback.status);
      setResolutionNotes(selectedFeedback.resolution_notes || '');
    }
  }, [selectedFeedback]);

  // Reset delete confirm when modal closes/opens
  useEffect(() => {
    if (!isOpen) {
      setShowDeleteConfirm(false);
    }
  }, [isOpen]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteFeedback(feedbackId);
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      // Error handled by hook
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || newStatus === selectedFeedback.status) return;

    setUpdating(true);
    try {
      await updateFeedback(feedbackId, newStatus, resolutionNotes);
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      // Error handled by hook
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getIssueTypeLabel = (type) => {
    const labels = {
      too_slow: { ar: 'بطيء جداً', en: 'Too Slow' },
      wrong_answer: { ar: 'إجابة خاطئة', en: 'Wrong Answer' },
      could_be_better: { ar: 'يمكن تحسينها', en: 'Could Be Better' },
      other: { ar: 'أخرى', en: 'Other' }
    };
    return labels[type]?.[language] || type;
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: { ar: 'مفتوح', en: 'Open' },
      in_progress: { ar: 'قيد المعالجة', en: 'In Progress' },
      resolved: { ar: 'تم الحل', en: 'Resolved' },
      wont_fix: { ar: 'لن يتم الإصلاح', en: "Won't Fix" }
    };
    return labels[status]?.[language] || status;
  };

  // Transform feedback data into message format for chat components
  const buildMessageObject = (feedback) => {
    if (!feedback) return null;

    const metadata = feedback.message_metadata || {};

    // Build raw_data from results if available
    const rawData = metadata.results?.map((row) => {
      const obj = {};
      (metadata.column_names || []).forEach((col, i) => {
        obj[col] = row[i];
      });
      return obj;
    }) || [];

    // chart_spec already contains vega_lite_spec, charts, etc. - pass it directly as visualization
    // Add raw_data if not present
    let visualization = null;
    if (feedback.chart_spec && Object.keys(feedback.chart_spec).length > 0) {
      visualization = {
        ...feedback.chart_spec,
        raw_data: feedback.chart_spec.raw_data || rawData
      };
    }

    return {
      id: feedback.message_id,
      content: feedback.message_content,
      sql: feedback.sql_query,
      execution_ms: feedback.sql_execution_ms,
      column_names: metadata.column_names || [],
      results: metadata.results || [],
      suggestions: metadata.suggestions || [],
      explanation: metadata.explanation,
      visualization: visualization,
      timestamp: formatDate(feedback.message_created_at)
    };
  };

  if (!isOpen) return null;

  const feedback = selectedFeedback;
  const IssueIcon = feedback ? ISSUE_TYPE_ICONS[feedback.issue_type] : MessageSquare;
  const StatusIcon = feedback ? STATUS_ICONS[feedback.status] : Clock;
  const messageObject = buildMessageObject(feedback);

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          $isRTL={isRTL}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <Title>
              {language === 'ar' ? 'تفاصيل البلاغ' : 'Report Details'}
            </Title>
            <CloseButton onClick={onClose}>
              <X />
            </CloseButton>
          </Header>

          {loading ? (
            <LoadingState>
              <Loader />
            </LoadingState>
          ) : error ? (
            <ErrorState>{error}</ErrorState>
          ) : feedback ? (
            <Content>
              {/* Status Badges */}
              <BadgeRow>
                <IssueTypeBadge $type={feedback.issue_type}>
                  <IssueIcon />
                  {getIssueTypeLabel(feedback.issue_type)}
                </IssueTypeBadge>
                <StatusBadge $status={feedback.status}>
                  <StatusIcon />
                  {getStatusLabel(feedback.status)}
                </StatusBadge>
              </BadgeRow>

              {/* Reporter Info */}
              <Section>
                <SectionTitle>
                  <User />
                  {language === 'ar' ? 'معلومات المُبلِغ' : 'Reporter Info'}
                </SectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <div className="label">
                      {language === 'ar' ? 'المستخدم' : 'User'}
                    </div>
                    <div className="value">
                      {feedback.reporter_display_name || feedback.reporter_name}
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">
                      {language === 'ar' ? 'تاريخ البلاغ' : 'Report Date'}
                    </div>
                    <div className="value">{formatDate(feedback.created_at)}</div>
                  </InfoItem>
                </InfoGrid>
              </Section>

              {/* Feedback Comment (user's complaint) */}
              {feedback.comment && (
                <FeedbackCommentBox>
                  <div className="feedback-label">
                    <MessageSquare size={14} />
                    {language === 'ar' ? 'وصف المشكلة من المستخدم' : 'User\'s Issue Description'}
                  </div>
                  <div className="feedback-text">{feedback.comment}</div>
                </FeedbackCommentBox>
              )}

              {/* Full Chat Preview - exactly like conversation */}
              <Section>
                <SectionTitle>
                  <MessageSquare />
                  {language === 'ar' ? 'المحادثة الكاملة' : 'Full Conversation'}
                </SectionTitle>

                <ChatPreviewContainer>
                  {/* User Question */}
                  {feedback.original_question && (
                    <UserMessageBubble>
                      <div className="content">{feedback.original_question}</div>
                      <div className="timestamp">{formatDate(feedback.question_asked_at)}</div>
                    </UserMessageBubble>
                  )}

                  {/* Bot Response - exactly like chat */}
                  {messageObject && (
                    <BotMessageContainer>
                      <Avatar>
                        <Bot />
                      </Avatar>
                      <BotBubble>
                        {/* Main Content */}
                        <div className="main-content">{messageObject.content}</div>

                        {/* SQL Display */}
                        {messageObject.sql && (
                          <SQLDisplay message={messageObject} />
                        )}

                        {/* Data Table */}
                        {messageObject.column_names?.length > 0 && messageObject.results?.length > 0 && (
                          <DataSection>
                            <div className="label">
                              <Table />
                              {language === 'ar' ? 'نتائج الاستعلام:' : 'Query Results:'}
                            </div>
                            <DataTable
                              columnNames={messageObject.column_names}
                              results={messageObject.results}
                            />
                          </DataSection>
                        )}

                        {/* Suggestions */}
                        {messageObject.suggestions?.length > 0 && (
                          <ContextualSuggestions
                            suggestions={messageObject.suggestions}
                            explanation={messageObject.explanation}
                          />
                        )}

                        {/* Chart Visualization */}
                        {messageObject.visualization && (
                          <VisualizationSection>
                            <div className="label">
                              <BarChart3 />
                              {language === 'ar' ? 'تصور البيانات:' : 'Data Visualization:'}
                            </div>
                            <DataVisualization message={messageObject} />
                          </VisualizationSection>
                        )}

                        <div className="timestamp">{messageObject.timestamp}</div>
                      </BotBubble>
                    </BotMessageContainer>
                  )}
                </ChatPreviewContainer>
              </Section>

              {/* Resolution Info (if resolved) */}
              {(feedback.status === 'resolved' || feedback.status === 'wont_fix') && feedback.resolution_notes && (
                <Section>
                  <SectionTitle>
                    <CheckCircle />
                    {language === 'ar' ? 'ملاحظات الحل' : 'Resolution Notes'}
                  </SectionTitle>
                  <ResolutionInfo>
                    <div style={{ marginBottom: 12 }}>{feedback.resolution_notes}</div>
                    {feedback.resolver_name && (
                      <InfoGrid>
                        <InfoItem>
                          <div className="label">
                            {language === 'ar' ? 'تم الحل بواسطة' : 'Resolved By'}
                          </div>
                          <div className="value">
                            {feedback.resolver_display_name || feedback.resolver_name}
                          </div>
                        </InfoItem>
                        <InfoItem>
                          <div className="label">
                            {language === 'ar' ? 'تاريخ الحل' : 'Resolved At'}
                          </div>
                          <div className="value">{formatDate(feedback.resolved_at)}</div>
                        </InfoItem>
                      </InfoGrid>
                    )}
                  </ResolutionInfo>
                </Section>
              )}

              {/* Admin Status Update */}
              {isAdmin && (
                <StatusUpdateSection>
                  <SectionTitle>
                    <RefreshCw />
                    {language === 'ar' ? 'تحديث الحالة' : 'Update Status'}
                  </SectionTitle>

                  <StatusSelect
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    <option value="open">{language === 'ar' ? 'مفتوح' : 'Open'}</option>
                    <option value="in_progress">{language === 'ar' ? 'قيد المعالجة' : 'In Progress'}</option>
                    <option value="resolved">{language === 'ar' ? 'تم الحل' : 'Resolved'}</option>
                    <option value="wont_fix">{language === 'ar' ? 'لن يتم الإصلاح' : "Won't Fix"}</option>
                  </StatusSelect>

                  <ResolutionTextarea
                    $isRTL={isRTL}
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder={language === 'ar'
                      ? 'أضف ملاحظات حول الحل أو سبب عدم الإصلاح...'
                      : 'Add notes about the resolution or reason for not fixing...'}
                  />

                  <UpdateButton
                    onClick={handleUpdateStatus}
                    disabled={updating || newStatus === feedback.status}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {updating ? (
                      <>
                        <Loader className="animate-spin" />
                        {language === 'ar' ? 'جاري التحديث...' : 'Updating...'}
                      </>
                    ) : (
                      <>
                        <Save />
                        {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                      </>
                    )}
                  </UpdateButton>

                  {/* Delete Button - Admin Only */}
                  {!showDeleteConfirm ? (
                    <DeleteButton
                      onClick={() => setShowDeleteConfirm(true)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Trash2 />
                      {language === 'ar' ? 'حذف البلاغ' : 'Delete Report'}
                    </DeleteButton>
                  ) : (
                    <ConfirmDialog
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <ConfirmMessage>
                        <AlertTriangle />
                        <div className="text">
                          {language === 'ar'
                            ? 'هل أنت متأكد من حذف هذا البلاغ؟ لا يمكن التراجع عن هذا الإجراء.'
                            : 'Are you sure you want to delete this report? This action cannot be undone.'}
                        </div>
                      </ConfirmMessage>
                      <ConfirmButtons>
                        <ConfirmButton
                          className="cancel"
                          onClick={() => setShowDeleteConfirm(false)}
                          whileHover={{ scale: 1.02 }}
                        >
                          {language === 'ar' ? 'إلغاء' : 'Cancel'}
                        </ConfirmButton>
                        <ConfirmButton
                          className="confirm"
                          onClick={handleDelete}
                          disabled={deleting}
                          whileHover={{ scale: 1.02 }}
                        >
                          {deleting ? (
                            <>
                              <Loader className="animate-spin" />
                              {language === 'ar' ? 'جاري الحذف...' : 'Deleting...'}
                            </>
                          ) : (
                            <>
                              <Trash2 />
                              {language === 'ar' ? 'نعم، احذف' : 'Yes, Delete'}
                            </>
                          )}
                        </ConfirmButton>
                      </ConfirmButtons>
                    </ConfirmDialog>
                  )}
                </StatusUpdateSection>
              )}
            </Content>
          ) : null}
        </Modal>
      </Overlay>
    </AnimatePresence>
  );
}

export default FeedbackDetailModal;
